/* eslint no-await-in-loop: "off" */
import {
  Button,
  CircularProgress,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Slider,
  Checkbox,
} from "@nextui-org/react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { IoChevronDown, IoLogOut, IoSearch } from "react-icons/io5";
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineStarBorder,
  MdMailOutline,
  MdOutlineSimCardDownload,
  MdOutlineCorporateFare,
  MdOutlineSwapVert,
} from "react-icons/md";
import { HiOutlineUserAdd } from "react-icons/hi";
import { CgExport } from "react-icons/cg";
import { useSelector } from "react-redux";
import { format, fromUnixTime, parseISO } from "date-fns";
import { BsArrowLeft } from "react-icons/bs";

import CalendarDropdown from "./CalendarDropdown";
import FilterDropdown from "./FilterDropdown";
import LanguageSwitcher from "./LanguageSwitcher";

import dashRoutes from "../routes/dash";
import { useAppContext } from "../contexts/AppContext";
import { parseArrayToCsv } from "../utils/csvUtils";
import { DownloadIcon, ShareIcon } from "../assets/icons/audio-icons";
import getConfig from "../conf/config";
import { transformFromUTC, transformFromUTCFromISO } from "../utils/dateUtils";
import { downloadCallAudioBlob } from "../utils/downloadCallAudio";

export const GradeSliderDropdown = ({
  label,
  sliderLabel,
  value,
  onChange = () => {},
  hideQuickActions = false,
  hideStar = false,
  excludeNA = null,
  onExcludeNAChange = null,
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [localValue, setLocalValue] = useState(value);

  // Update local value when prop value changes
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChangeEnd = (newValue) => {
    // Only trigger onChange when user finishes dragging
    setLocalValue(newValue);
    onChange(newValue);
  };

  const handleQuickAction = (quickValue) => {
    setLocalValue(quickValue);
    onChange(quickValue);
  };

  return (
    <Popover
      placement="bottom"
      showArrow
      offset={10}
      triggerScaleOnOpen={false}
      isOpen={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
    >
      <PopoverTrigger>
        <div className="hover:bg-black/10 duration-400 rounded-md px-[12px] py-[8px] flex items-center cursor-pointer gap-[8px]">
          {!hideStar && <MdOutlineStarBorder size={20} />}
          <span className="whitespace-nowrap leading-3 text-[14px]">{t(label)}</span>
          <IoChevronDown size={16} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4">
        <div>
          <Slider
            size="sm"
            label={t(sliderLabel)}
            showTooltip
            step={1}
            maxValue={10}
            minValue={0}
            value={localValue}
            onChange={setLocalValue} // Update local state only (no API call)
            onChangeEnd={handleChangeEnd} // Trigger API call only when dragging ends
            className="min-w-[220px]"
            marks={[
              {
                value: 0,
                label: "0",
              },
              {
                value: 10,
                label: "10",
              },
            ]}
            classNames={{
              label: "text-[16px] text-primary mb-3",
              value: "text-xs text-primary mb-3 duration-500",
            }}
          />
          {!hideQuickActions && (
            <div className="flex gap-2 pt-3 items-center justify-between">
              <div
                className="flex grow py-1 rounded-md text-white text-center cursor-pointer items-center justify-center bg-grade-low"
                onClick={() => handleQuickAction([0.1, 5])}
                onKeyDown={() => handleQuickAction([0.1, 5])}
              >
                0 - 5
              </div>
              <div
                className="flex grow py-1 rounded-md text-white text-center cursor-pointer items-center justify-center bg-grade-medium"
                onClick={() => handleQuickAction([5, 7])}
                onKeyDown={() => handleQuickAction([5, 7])}
              >
                5 - 7
              </div>
              <div
                className="flex grow py-1 rounded-md text-white text-center cursor-pointer items-center justify-center bg-grade-high"
                onClick={() => handleQuickAction([7, 10])}
                onKeyDown={() => handleQuickAction([7, 10])}
              >
                7 - 10
              </div>
            </div>
          )}
          {onExcludeNAChange && (
            <div className="pt-3">
              <Checkbox
                size="sm"
                isSelected={!!excludeNA}
                onValueChange={onExcludeNAChange}
              >
                <span className="text-xs">{t("Exclude N.A from results")}</span>
              </Checkbox>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Topbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);
  const { calls, selectedCalls = [] } = useSelector((state) => state.calls.calls);
  const { company_config, loading: algoliaLoading } = useSelector((state) => state.algolia.algolia);
  const { listData } = useSelector((state) => state.lists.lists);
  const { available_departments } = useSelector((state) => state.analytics.analytics);
  const { operators } = useSelector((state) => state.operators.operators);
  const { sales } = useSelector((state) => state.financial_insights.financial_insights);
  const {
    setOutgoing,
    setIncoming,
    setSearchText,
    preventSearchReset,
    operatorsWithMetrics,
    setSelectedDepartment,
    callsDateRange,
    setCallsDateRange,
    financialDateRange,
    setFinancialDateRange,
    searchText,
    setTeamGrades,
    handleOnGradeChange,
    individualGrades,
    setIsInviteMemberModalDisplayed,
    dashboardDepartment,
    setDashboardDepartment,
    dashboardDirection,
    setDashboardDirection,
    representativesDepartment,
    setRepresentativesDepartment,
  } = useAppContext();
  const [groupSelected, setGroupSelected] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState();
  const [teamGradeValue, setTeamGradeValue] = React.useState([0, 10]);
  const [individualGradeValue, setIndividualGradeValue] = React.useState([0, 10]);
  const excludeNAValue = !!individualGrades?.exclude_na;
  const [hasError, sethasError] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const nbSelectedCalls = selectedCalls.length;
  const hasSelectedCalls = nbSelectedCalls > 0;

  const navigate = useNavigate();
  // useEffect(() => {
  // setTeamGrades({
  //   min_team_grade: teamGradeValue[0],
  //   max_team_grade: teamGradeValue[1],
  // });
  // handleOnGradeChange("individual", {
  //   min_individual_grade: individualGradeValue[0],
  //   max_individual_grade: individualGradeValue[1],
  // });
  // }, [teamGradeValue, individualGradeValue]);

  useEffect(() => {
    if (groupSelected.includes("incoming")) {
      setIncoming(true);
    } else {
      setIncoming(false);
    }
    if (groupSelected.includes("outgoing")) {
      setOutgoing(true);
    } else {
      setOutgoing(false);
    }
  }, [groupSelected]);

  const isFinancialPage = location.pathname.includes("/financials");
  const dateRangeToUse = isFinancialPage ? financialDateRange : callsDateRange;
  const setDateRangeToUse = isFinancialPage ? setFinancialDateRange : setCallsDateRange;

  // Prevent the search text to persist when changing pages

  useEffect(() => {
    if (!preventSearchReset) {
      setSearchText("");
    }

    // Reset grades on page change
    setTeamGradeValue([0, 10]);
    setIndividualGradeValue([0, 10]);
    setTeamGrades({ min_team_grade: 0, max_team_grade: 10 });
    handleOnGradeChange("individual", {
      min_individual_grade: 0,
      max_individual_grade: 10,
      exclude_na: false,
    });
  }, [location.pathname]);

  const handleOnSelectFilterOption = (selected) => {
    const keys = Array.from(selected);
    if (keys.length > 0) {
      setSelectedKeys(keys);
      setSelectedDepartment(keys);
      return;
    }
    setSelectedKeys(["ALL"]);
    setSelectedDepartment(["ALL"]);
  };

  const isCallsPage =
    location.pathname &&
    location.pathname.includes("/calls") &&
    !location.pathname.includes("/calls/lists");
  const isDebriefsPage = location.pathname && location.pathname.includes("/debriefs");
  const isInsightsPage = location.pathname && location.pathname.includes("/insights");
  const isListsPage = location.pathname === "/calls/lists";
  const isListDetailsPage =
    location.pathname !== "/calls/lists" && location.pathname.includes("/calls/lists");
  const isFinancialsPage = location.pathname === "/financials";
  const isDashboard = location.pathname === "/dashboard";
  const isRepresentativePage = location.pathname === "/representatives";
  const isFinancialDetails = location.pathname === "/financials/details";
  const isAdminSettings = location.pathname.includes("/settings");
  const isLiveContentSearchPage =
    location.pathname && location.pathname.includes("/live-content-search");
  const isTrainingPage = location.pathname && location.pathname.includes("/training");

  const getPageName = () => {
    if (isDashboard) {
      return `${t("misc.hello")} ${user?.first_name || ""}`;
    }

    if (isListDetailsPage) {
      return listData?.name || "";
    }

    let route = dashRoutes.find((route) => route.path === location.pathname);
    if (!route) {
      // check children routes
      const childrenAndSubroutes = [];
      let cpt = 0;
      while (cpt < dashRoutes.length) {
        const route = dashRoutes[cpt];
        childrenAndSubroutes.push(...(route?.children || []));
        cpt++;
      }

      route = childrenAndSubroutes.find((route) => route.path === location.pathname);
    }
    return route?.name;
  };

  const onNavigationGoBack = () => {
    navigate("/calls/lists");
  };

  const downloadAudioFile = async (callId) => {
    return downloadCallAudioBlob(callId);
  };

  const formatAudioFileName = (item) => {
    const dateBloc = new Date(item.call_timestamp * 1000)
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `${item?.operator_name.toUpperCase()}_${dateBloc}.wav`;
  };

  const formatMultiCallFileName = (fileExtension) => {
    const dateBloc = new Date()
      .toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
      .replace(/,/g, "")
      .replace(/:/g, "_");

    return `export_${dateBloc}.${fileExtension}`;
  };

  const exportAudioFilesZipped = async () => {
    if (!selectedCalls || selectedCalls.length <= 0) {
      return;
    }
    const zipFilename = formatMultiCallFileName("zip");
    const zip = new JSZip();
    let cpt = 0;
    setIsExportLoading(true);
    while (cpt < selectedCalls.length) {
      const selectedCall = selectedCalls[cpt];
      const filename = formatAudioFileName(selectedCall);
      const audioBlob = await downloadAudioFile(selectedCall.id);
      zip.file(filename, audioBlob, { binary: true });
      cpt++;
    }

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, zipFilename);
      setIsExportLoading(false);
    });
  };

  const handleOnExport = () => {
    if (isCallsPage || isLiveContentSearchPage) {
      const filename = `calls_${format(new Date(dateRangeToUse.startDate), "MM-dd-yyyy")}_${format(
        new Date(dateRangeToUse.endDate),
        "MM-dd-yyyy"
      )}.csv`;
      // parseArrayToCsv(hasSelectedCalls ? selectedCalls : calls, filename);
      exportAudioFilesZipped();
      return;
    }

    switch (location.pathname) {
      case "/calls":
        {
          const filename = `calls_${format(
            new Date(dateRangeToUse.startDate),
            "MM-dd-yyyy"
          )}_${format(new Date(dateRangeToUse.endDate), "MM-dd-yyyy")}.csv`;
          // parseArrayToCsv(hasSelectedCalls ? selectedCalls : calls, filename);
          exportAudioFilesZipped();
        }
        break;
      case "/representatives":
        {
          if (!operatorsWithMetrics || operatorsWithMetrics.length === 0) {
            return;
          }
          const filename = `operators_${format(
            new Date(dateRangeToUse.startDate),
            "MM-dd-yyyy"
          )}_${format(new Date(dateRangeToUse.endDate), "MM-dd-yyyy")}.csv`;
          parseArrayToCsv(operatorsWithMetrics, filename);
        }
        break;
      case "/financials/details":
        {
          const filename = `financials_${format(
            new Date(dateRangeToUse.startDate),
            "MM-dd-yyyy"
          )}_${format(new Date(dateRangeToUse.endDate), "MM-dd-yyyy")}.csv`;
          parseArrayToCsv(sales, filename);
        }
        break;

      default:
        break;
    }
  };

  const onShareSelected = () => {
    if (!hasSelectedCalls) {
      return;
    }

    const baseUrl = getConfig().BASE_URL;

    const links = selectedCalls.map((selectedCall) => {
      return `- ${baseUrl}/calls/${selectedCall?.id}`;
    });

    const mailString = links.join("\n");

    const mailtoLink = `mailto:?subject=Partage de Calls Audio&body=${encodeURIComponent(
      `Voici les calls :
${mailString}`
    )}`;
    window.open(mailtoLink, "_blank");
  };

  const onShareSelectedXLSX = () => {
    if (!hasSelectedCalls) {
      return;
    }

    // const baseUrl = getConfig().BASE_URL;
    const baseUrl = "https://audio.app.easierlabs.ai";

    const operatorIdsToFilterOut = [
      // "6uhJ9j2ziiQnhq6cuitK",
      // "d4hjY3BlEP0ps6h4AQvj",
      // "EML8yIquZRtbb8vklXuz",
    ];

    const rows = selectedCalls
      ?.filter((call) => !operatorIdsToFilterOut.includes(call?.operator_id))
      ?.map((selectedCall) => {
        // const audioUrl = selectedCall?.audio_url;
        const callLink = `${baseUrl}/calls/${selectedCall?.id}`;
        const localisationsById = {
          "9a8j5obKmJbBOvEoOyID": "MAURICE",
          AoREEREk93cN3bXetXOH: "CROSNE",
          wMrxVxAlshoS9LzAe9RA: "JOUE-LES-TOURS",
        };

        const localisation_name = localisationsById?.[selectedCall?.localisation_id] || "";

        let callDate = "";
        if (selectedCall?.call_timestamp) {
          if (Number.isInteger(selectedCall.call_timestamp)) {
            callDate = format(
              transformFromUTC(selectedCall.call_timestamp, "Europe/Paris"),
              "yyyy-MM-dd"
            );
          } else {
            callDate = format(
              transformFromUTCFromISO(selectedCall.call_timestamp, "Europe/Paris"),
              "yyyy-MM-dd"
            );
          }
        }

        return {
          id: selectedCall.id,
          // audio_dl: { f: `HYPERLINK("${audioUrl}")` },
          date: callDate,
          call_link: { f: `HYPERLINK("${callLink}")` },
          operator_name: selectedCall?.operator_name || "",
          client_number: selectedCall?.client_number || "",
          call_summary: selectedCall?.analysis?.call_summary || "",
          // from: selectedCall.incoming ? "incoming" : "outgoing",
          // grade: selectedCall?.analysis?.call_overall_grade?.toFixed(1) || -1,
          // dynamism: selectedCall?.analysis?.dynamism || -1,
          // call_structure_respect: selectedCall?.analysis?.call_structure_respect || -1,
          client_sentiment: selectedCall?.analysis?.client_sentiment || "",
          // customer_service_tone: selectedCall?.analysis?.customer_service_tone || "",
          // team: selectedCall?.team || "",
          department: selectedCall?.department || "",
          time: selectedCall?.call_timestamp
            ? Number.isInteger(selectedCall.call_timestamp)
              ? format(transformFromUTC(selectedCall.call_timestamp, "Europe/Paris"), "HH:mm:ss")
              : format(
                  transformFromUTCFromISO(selectedCall.call_timestamp, "Europe/Paris"),
                  "HH:mm:ss"
                )
            : "",
          SITE: localisation_name,
        };
      });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Calls");
    const fileName = formatMultiCallFileName("xlsx");
    XLSX.writeFile(workbook, fileName, { compression: false });
  };

  const handleClearSearch = () => {
    setSearchText(""); // Clear the searchText state
  };

  // "all" first so the filter can always be cleared from the list itself.
  const departmentOptions = [
    { key: "all", label: "All departments" },
    ...(available_departments || []).map((department) => ({
      key: department.id,
      label: department.name,
    })),
  ];
  // Operators page: departments come from the loaded operators themselves, so
  // the list only offers departments the user can see (and costs no request).
  const representativeDepartmentOptions = React.useMemo(() => {
    const namesById = {};
    (operatorsWithMetrics || []).forEach((operator) => {
      if (operator?.department_id && !namesById[operator.department_id]) {
        namesById[operator.department_id] = operator.department || operator.department_id;
      }
    });
    return [
      { key: "all", label: "All departments" },
      ...Object.keys(namesById)
        .map((id) => ({ key: id, label: namesById[id] }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ];
  }, [operatorsWithMetrics]);
  const showRepresentativeDepartmentFilter =
    representativeDepartmentOptions.length > 2 || representativesDepartment !== "all";

  const directionOptions = [
    { key: "all", label: "All directions" },
    { key: "incoming", label: "Incoming" },
    { key: "outgoing", label: "Outgoing" },
  ];

  const getAvailableActions = () => {
    const res = [];

    if (isRepresentativePage) {
      res.push("team-grade");
    }
    if (isAdminSettings) {
      res.push("invite-member");
    }

    if (!isFinancialDetails && !isAdminSettings && !isLiveContentSearchPage) {
      res.push("individual-grade");
    }

    if (!isListDetailsPage && !isAdminSettings) {
      res.push("dates");
    }

    if (isCallsPage || isLiveContentSearchPage || isListDetailsPage) {
      res.push("share");
    }

    if (!isAdminSettings) {
      res.push("export");
    }

    return res;
  };

  const renderAction = (actionLabel) => {
    // const disableInvitationModal = false;
    const disableInvitationModal = company_config?.feature_flags?.hide_invitation_btn || false;

    switch (actionLabel) {
      case "team-grade":
        return (
          <GradeSliderDropdown
            label="Team grade"
            sliderLabel="Select grade"
            value={teamGradeValue}
            onChange={(teamGradeValues) => {
              setTeamGradeValue(teamGradeValues);
              handleOnGradeChange("team", {
                min_team_grade: teamGradeValues[0],
                max_team_grade: teamGradeValues[1],
              });
            }}
          />
        );
      case "individual-grade":
        return (
          <GradeSliderDropdown
            label={isCallsPage ? "Grade" : "Individual Grade"}
            sliderLabel="Select grade"
            value={individualGradeValue}
            onChange={(individualGradeValues) => {
              setIndividualGradeValue(individualGradeValues);
              handleOnGradeChange("individual", {
                min_individual_grade: individualGradeValues[0],
                max_individual_grade: individualGradeValues[1],
                exclude_na: excludeNAValue,
              });
            }}
            excludeNA={excludeNAValue}
            onExcludeNAChange={(newExcludeNA) => {
              handleOnGradeChange("individual", {
                min_individual_grade: individualGradeValue[0],
                max_individual_grade: individualGradeValue[1],
                exclude_na: newExcludeNA,
              });
            }}
          />
        );
      case "dates":
        return (
          <div className="bg-background rounded-md">
            <CalendarDropdown
              dateRangeValue={dateRangeToUse}
              onDateRangeChange={(dates) => {
                setDateRangeToUse(dates);
              }}
            />
          </div>
        );
      case "share":
        return (
          <div
            onClick={onShareSelected}
            onKeyDown={onShareSelected}
            // onClick={onShareSelectedXLSX}
            // onKeyDown={onShareSelectedXLSX}
            className={`${
              hasSelectedCalls
                ? "cursor-pointer hover:bg-black/10 duration-400"
                : "cursor-not-allowed text-[#171717]/[0.6]"
            } px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]`}
          >
            {hasSelectedCalls ? `${t("Share")} (${nbSelectedCalls})` : t("Share")}
            <MdMailOutline
              size={20}
              className="ml-2"
            />
          </div>
        );
      case "export":
        return (
          <div
            onClick={handleOnExport}
            onKeyDown={handleOnExport}
            className="cursor-pointer hover:bg-black/10 duration-400 px-[4px] py-[8px] rounded-md flex items-center text-sm text-[#171717]"
          >
            {isExportLoading && (
              <CircularProgress
                size="sm"
                aria-label="Loading..."
                color="primary"
                className="mr-[16px]"
              />
            )}
            {hasSelectedCalls ? `${t("Download")} (${nbSelectedCalls})` : t("Download")}
            <MdOutlineSimCardDownload
              size={20}
              color="#737373"
              className="ml-2"
            />
          </div>
        );
      case "invite-member":
        return (
          <div
            onClick={() => {
              if (disableInvitationModal) {
                return;
              }
              setIsInviteMemberModalDisplayed(true);
            }}
            onKeyDown={() => {
              if (disableInvitationModal) {
                return;
              }
              setIsInviteMemberModalDisplayed(true);
            }}
            className={`${
              disableInvitationModal ? "opacity-[0.6] cursor-default" : "cursor-pointer"
            } py-[8px] px-[4px] py-[8px] bg-primary rounded-md flex items-center text-sm text-white`}
          >
            {t("Invite a new member to join the team")}
            <HiOutlineUserAdd
              size={16}
              color="white"
              className="ml-2"
            />
          </div>
        );
      default:
        break;
    }
  };

  const renderActions = () => {
    const actions = getAvailableActions();

    return (
      <div className="flex items-center gap-[8px]">
        {actions.map((action, actionIndex) => {
          const isLastAction = actionIndex === action.length - 1;
          return (
            <div className="border-1 border-solid border-[#E5E5E5] rounded-md text-[#404040]/[0.6]">
              {renderAction(action)}
            </div>
          );
        })}
      </div>
    );
  };

  if (isTrainingPage) {
    return null; // Don't render the Topbar on the training page
  }

  return (
    <div
      className={`flex-1 ${
        isDashboard || isFinancialsPage || isDebriefsPage ? "flex items-center" : ""
      } sticky top-0 right-0 z-50 bg-background w-full px-4 lg:px-6 ${
        !isDashboard && !isDebriefsPage ? "pt-[72px]" : "pt-[40px]"
      }`}
    >
      <div className="flex flex-col">
        {isListDetailsPage && (
          <div
            className="cursor-pointer flex items-center mb-2 text-[#888888]"
            onClick={onNavigationGoBack}
            onKeyDown={onNavigationGoBack}
          >
            <BsArrowLeft size={20} />
            <span className="ml-2">{t("Back")}</span>
          </div>
        )}
        {!isAdminSettings && !isDebriefsPage && !isInsightsPage && (
          <h1 className="whitespace-nowrap text-[36px] font-semibold text-primary">
            {t(getPageName())}
          </h1>
        )}
      </div>
      {!isFinancialsPage &&
        !isListsPage &&
        !isCallsPage &&
        !isLiveContentSearchPage &&
        !isAdminSettings &&
        !isDebriefsPage &&
        !isInsightsPage && (
          <div className="flex items-center justify-end w-auto gap-x-3 ml-auto">
            {isDashboard && (
              <div className="mt-[16px] flex items-center gap-x-3 flex-wrap justify-end gap-y-2">
                {departmentOptions.length > 1 && (
                  <FilterDropdown
                    icon={MdOutlineCorporateFare}
                    placeholder="All departments"
                    options={departmentOptions}
                    value={dashboardDepartment}
                    onChange={setDashboardDepartment}
                  />
                )}
                <FilterDropdown
                  icon={MdOutlineSwapVert}
                  placeholder="All directions"
                  options={directionOptions}
                  value={dashboardDirection}
                  onChange={setDashboardDirection}
                />
                <CalendarDropdown
                  dateRangeValue={callsDateRange}
                  onDateRangeChange={(dates) => {
                    setCallsDateRange(dates);
                  }}
                />
              </div>
            )}
            {isRepresentativePage && showRepresentativeDepartmentFilter && (
              <div className="mt-[16px] shrink-0">
                <FilterDropdown
                  icon={MdOutlineCorporateFare}
                  placeholder="All departments"
                  options={representativeDepartmentOptions}
                  value={representativesDepartment}
                  onChange={setRepresentativesDepartment}
                />
              </div>
            )}
            {!isDashboard && (
              <div className="flex w-full items-center p-1.5 px-2 mt-[16px] bg-white rounded-md">
                <div className="flex items-center space-x-2 w-full">
                  <Input
                    placeholder={t("Search")}
                    onClear={handleClearSearch}
                    radius="full"
                    value={searchText}
                    startContent={
                      <IoSearch
                        color="#737373"
                        className="w-4 h-4"
                      />
                    }
                    className="flex-1 min-w-[250px] grow-1"
                    classNames={{
                      inputWrapper: ["bg-white border-none rounded-md shadow-none"],
                    }}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                  {renderActions()}
                </div>
              </div>
            )}
          </div>
        )}
    </div>
  );
};

export default Topbar;
